import React from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

interface GroupData {
  groupId: string;
  groupName: string;
  count: number;
}

interface GroupWiseChangesChartProps {
  data: GroupData[];
  title: string;
  chartId: string;
  barColor?: string;
  onBarClick?: (groupId: string, groupName: string) => void;
}

// Color palette for groups
const groupColors = [
  'rgba(59, 130, 246, 0.8)',   // blue
  'rgba(16, 185, 129, 0.8)',   // green
  'rgba(245, 158, 11, 0.8)',   // amber
  'rgba(147, 51, 234, 0.8)',   // purple
  'rgba(239, 68, 68, 0.8)',    // red
  'rgba(236, 72, 153, 0.8)',   // pink
  'rgba(6, 182, 212, 0.8)',    // cyan
  'rgba(249, 115, 22, 0.8)',   // orange
  'rgba(34, 197, 94, 0.8)',    // emerald
  'rgba(99, 102, 241, 0.8)',   // indigo
];

const GroupWiseChangesChart: React.FC<GroupWiseChangesChartProps> = ({
  data,
  title,
  chartId,
  barColor,
  onBarClick
}) => {
  if (data.length === 0) {
    return (
      <div id={chartId} className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
        <div className="h-48 flex items-center justify-center text-gray-500">
          No data available
        </div>
      </div>
    );
  }

  // Take top 10 groups
  const topData = data.slice(0, 10);
  const labels = topData.map(d =>
    d.groupName.length > 20 ? d.groupName.substring(0, 17) + '...' : d.groupName
  );
  const values = topData.map(d => d.count);
  const colors = barColor
    ? topData.map(() => barColor)
    : topData.map((_, index) => groupColors[index % groupColors.length]);
  const total = values.reduce((sum, val) => sum + val, 0);

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Change Requests',
        data: values,
        backgroundColor: colors,
        borderColor: colors.map(c => c.replace('0.8', '1')),
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y' as const,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items: any[]) => {
            if (items.length > 0) {
              const index = items[0].dataIndex;
              return topData[index]?.groupName || '';
            }
            return '';
          },
          label: (context: any) => {
            const value = context.raw as number;
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${value} changes (${percentage}%)`;
          }
        }
      },
      datalabels: {
        display: (context: any) => context.dataset.data[context.dataIndex] > 0,
        color: '#fff',
        font: {
          weight: 'bold' as const,
          size: 11,
        },
        anchor: 'center' as const,
        align: 'center' as const,
        formatter: (value: number) => value > 0 ? value : '',
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { display: false },
        ticks: { precision: 0 }
      },
      y: {
        grid: { display: false }
      }
    },
    onClick: (_event: any, elements: any[]) => {
      if (elements.length > 0 && onBarClick) {
        const index = elements[0].index;
        const group = topData[index];
        if (group) {
          onBarClick(group.groupId, group.groupName);
        }
      }
    },
  };

  // Calculate dynamic height based on number of groups
  const chartHeight = Math.max(200, Math.min(350, topData.length * 35));

  return (
    <div id={chartId} className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        <span className="text-xs text-gray-500">Click to filter</span>
      </div>
      <div style={{ height: `${chartHeight}px` }}>
        <Bar data={chartData} options={options} />
      </div>
      {data.length > 10 && (
        <div className="mt-2 text-xs text-gray-500 text-center">
          Showing top 10 of {data.length} groups
        </div>
      )}
    </div>
  );
};

export default GroupWiseChangesChart;
